import React, { useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Search, ChevronDown, ChevronUp, Award, Mail, Phone } from 'lucide-react';
import CommonLoader from '../CommonLoader';
import CustomDatePicker from '../dashboard/CustomDatePicker';
import Pagination from '../Pagination';
import calender_empty from '../../assets/img/calender_emptty.svg';
import noslotts from '../../assets/img/colorkit (1).svg';
import { getFileURL } from '../../utils/fileURL';
import MasterLabel from '../common/MasterLabel';
import { useMasterLabel } from '../../hooks/useMasterLabel';

// Per-row category label inside the category dropdown (rules-of-hooks workaround).
const CategoryRowLabel = ({ cat }) => {
    const label = useMasterLabel('masterSpecialtyCategory', cat?.spcategoryId, 'spcategoryName', cat?.spcategoryName || '-');
    return <span>{label}</span>;
};

const AllDoctorMode = ({
    // Category State
    selectedCategory,
    isCategoryDropdownOpen,
    categoryDropdownRef,
    spCategoryList,
    spCategoryListLoading,
    setIsCategoryDropdownOpen,
    handleCategorySelect,

    // State
    selectedSpecialtyId,
    selectedSpecialty,
    selectedDate,
    currentPage,
    itemsPerPage,
    isSpecialtyDropdownOpen,
    specialtySearchQuery,
    specialtySearchError,
    specialtyDropdownRef,
    specialtySearchInputRef,
    openDoctorDrawer,
    doctorSelections,
    selectedDoctorForSlots,
    availableDoctors,
    availableDoctorsMetadata,
    isAvailableDoctorsLoading,
    singleDaySlots,
    isSingleDaySlotsLoading,
    specialties,
    specialtyLoading,
    today,

    // Setters
    setSelectedSpecialtyId,
    setSelectedSpecialty,
    setSelectedDate,
    setCurrentPage,
    setItemsPerPage,
    setIsSpecialtyDropdownOpen,
    setSpecialtySearchQuery,
    setOpenDoctorDrawer,
    setDoctorSelections,
    setSelectedDoctorForSlots,
    setSelectedSlot,
    setSelectedAddressId,
    setSelectedAppointmentType,
    setSelectedSpeciality,
    setSelectedDoctorId,
    setIsModalOpen,

    // Handlers
    handleSpecialtySearchChange,
    handleSpecialtySelect,

    // Dispatchers
    dispatch,
    getSingleDaySlots,
    getDoctorById,
    getSingleCustomPrice,

    // Data
    systemTimeZone,
    selectedPatient,
    Id,
    isFromAllDoctorMode,
    addDays,

    // Translation
    t,
    MAX_SPECIALTY_SEARCH_LENGTH
}) => {
    // Category search state
    const [categorySearchQuery, setCategorySearchQuery] = useState('');
    const categorySearchInputRef = useRef(null);

    // Filter categories locally by search
    const filteredCategoryList = (spCategoryList || []).filter(cat =>
        !categorySearchQuery.trim() || (cat.spcategoryName || '').toLowerCase().includes(categorySearchQuery.trim().toLowerCase())
    );

    // Reset category search when dropdown closes
    useEffect(() => {
        if (!isCategoryDropdownOpen) {
            setCategorySearchQuery('');
        }
    }, [isCategoryDropdownOpen]);

    // Auto-fetch slots when both appointment type and address are selected
    useEffect(() => {
        // Find the currently open doctor drawer
        if (openDoctorDrawer && doctorSelections[openDoctorDrawer]) {
            const currentSelections = doctorSelections[openDoctorDrawer];
            // Only fetch when both appointment type and address are selected
            if (currentSelections.appointmentType && currentSelections.address && selectedDate && selectedSpecialtyId) {
                const doctor = availableDoctors?.find(d => (d.doctorId || d._id) === openDoctorDrawer);
                if (doctor) {
                    // Only fetch if we don't already have slots for this doctor with the same selections
                    const needsFetch = !selectedDoctorForSlots ||
                        selectedDoctorForSlots.doctorId !== openDoctorDrawer ||
                        selectedDoctorForSlots.appointmentType?.typeId !== currentSelections.appointmentType ||
                        selectedDoctorForSlots.address?.addressId !== currentSelections.address;

                    if (needsFetch) {
                        dispatch(getSingleDaySlots({
                            doctorId: openDoctorDrawer,
                            date: selectedDate,
                            timeZone: systemTimeZone,
                            specialtyId: selectedSpecialtyId,
                            appointmentTypeId: currentSelections.appointmentType,
                            addressId: currentSelections.address,
                            isReschedulingProcess: true
                        }));

                        setSelectedDoctorForSlots({
                            doctorId: openDoctorDrawer,
                            doctor: doctor,
                            appointmentType: doctor.appointmentTypes?.find(apt => apt.typeId === currentSelections.appointmentType),
                            address: doctor.addresses?.find(addr => addr.addressId === currentSelections.address),
                            specialtyId: selectedSpecialtyId
                        });
                    }
                }
            } else {
                // Clear selectedDoctorForSlots if selections are incomplete
                if (selectedDoctorForSlots && selectedDoctorForSlots.doctorId === openDoctorDrawer) {
                    setSelectedDoctorForSlots(null);
                }
            }
        }
    }, [doctorSelections, openDoctorDrawer, selectedDate, selectedSpecialtyId, availableDoctors, selectedDoctorForSlots, dispatch, getSingleDaySlots, systemTimeZone]);

    return (
        <>
            {/* All Doctor Mode Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-3" data-testid="all-doctor-mode-filters">
                {/* Category Dropdown */}
                <div data-testid="all-doctor-mode-category-field-container">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="all-doctor-mode-category-label">{t('patientappointment.category', 'Category')}</label>
                    <div ref={categoryDropdownRef} className="relative w-full" data-testid="all-doctor-mode-category-wrapper">
                        <button
                            type="button"
                            onClick={() => !spCategoryListLoading && setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                            disabled={spCategoryListLoading}
                            className={`w-full px-4 py-3 border rounded-xl flex justify-between items-center font-medium transition text-sm
                            ${spCategoryListLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'text-gray-700 bg-white border-gray-200 hover:border-[#4ABDBD] cursor-pointer focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none'}`}
                            data-testid="all-doctor-mode-category-button"
                        >
                            <span className="truncate w-[85%] text-left" data-testid="all-doctor-mode-category-selected-text">
                                {selectedCategory
                                    ? <MasterLabel type="masterSpecialtyCategory" id={selectedCategory.spcategoryId} field="spcategoryName" fallback={selectedCategory.spcategoryName} />
                                    : t('patientappointment.select_category', 'Select Category')}
                            </span>
                            <span className="ml-2 flex items-center">
                                {spCategoryListLoading ? (
                                    <span className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" data-testid="all-doctor-mode-category-loading-spinner"></span>
                                ) : isCategoryDropdownOpen ? (
                                    <ChevronUp size={18} className="text-gray-500" data-testid="all-doctor-mode-category-chevron-up-icon" />
                                ) : (
                                    <ChevronDown size={18} className="text-gray-500" data-testid="all-doctor-mode-category-chevron-down-icon" />
                                )}
                            </span>
                        </button>

                        {isCategoryDropdownOpen && (
                            <div className="absolute left-0 mt-2 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden" data-testid="all-doctor-mode-category-dropdown">
                                {/* Search */}
                                <div className="p-2 border-b border-gray-200">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-testid="all-doctor-mode-category-search-icon" />
                                        <input
                                            ref={categorySearchInputRef}
                                            type="text"
                                            value={categorySearchQuery}
                                            onChange={(e) => setCategorySearchQuery(e.target.value)}
                                            placeholder={t('patientappointment.search_category', 'Search category...')}
                                            className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                            autoFocus
                                            data-testid="all-doctor-mode-category-search-input"
                                        />
                                    </div>
                                </div>
                                {spCategoryListLoading && (
                                    <div className="p-4 flex justify-center" data-testid="all-doctor-mode-category-list-loading">
                                        <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                    </div>
                                )}
                                {!spCategoryListLoading && (
                                    <ul className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-[#4ABDBD] scrollbar-track-gray-100" data-testid="all-doctor-mode-category-list">
                                        {filteredCategoryList.length === 0 ? (
                                            <li className="px-4 py-3 text-gray-500 text-center" data-testid="all-doctor-mode-no-categories-text">
                                                {t('patientappointment.no_categories_found', 'No categories found')}
                                            </li>
                                        ) : (
                                            filteredCategoryList.map((cat, idx) => (
                                                <li
                                                    key={cat.spcategoryId || idx}
                                                    onClick={() => handleCategorySelect(cat)}
                                                    className={`px-4 py-2.5 transition flex justify-between items-center
                                                    ${idx !== filteredCategoryList.length - 1 ? 'border-b border-gray-100' : ''}
                                                    cursor-pointer hover:bg-[#4ABDBD] hover:text-white`}
                                                    data-testid={`all-doctor-mode-category-option-${cat.spcategoryId || idx}`}
                                                >
                                                    <CategoryRowLabel cat={cat} />
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Specialty Dropdown */}
                <div data-testid="all-doctor-mode-specialty-field-container">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="all-doctor-mode-specialty-label">{t('patientappointment.all_specialties', 'All Specialties')} <span className="text-red-500">*</span></label>
                    <div ref={specialtyDropdownRef} className="relative w-full" data-testid="all-doctor-mode-specialty-wrapper">
                        <button
                            type="button"
                            onClick={() => !specialtyLoading && selectedCategory && setIsSpecialtyDropdownOpen(!isSpecialtyDropdownOpen)}
                            disabled={specialtyLoading || !selectedCategory}
                            className={`w-full px-4 py-3 border rounded-xl flex justify-between items-center font-medium transition text-sm
                            ${(specialtyLoading || !selectedCategory) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'text-gray-700 bg-white border-gray-200 hover:border-[#4ABDBD] cursor-pointer focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none'}`}
                            data-testid="all-doctor-mode-specialty-button"
                        >
                            <span className="truncate w-[85%] text-left" data-testid="all-doctor-mode-specialty-selected-text">
                                {!selectedCategory
                                    ? t('patientappointment.select_category_first', 'Select category first')
                                    : selectedSpecialty
                                        ? selectedSpecialty.name || t('patientappointment.select_specialty', 'Select Specialty')
                                        : t('patientappointment.select_specialty', 'Select Specialty')}
                            </span>
                            <span className="ml-2 flex items-center">
                                {specialtyLoading ? (
                                    <span className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" data-testid="all-doctor-mode-specialty-loading-spinner"></span>
                                ) : isSpecialtyDropdownOpen ? (
                                    <ChevronUp size={18} className="text-gray-500" data-testid="all-doctor-mode-specialty-chevron-up-icon" />
                                ) : (
                                    <ChevronDown size={18} className="text-gray-500" data-testid="all-doctor-mode-specialty-chevron-down-icon" />
                                )}
                            </span>
                        </button>

                        {isSpecialtyDropdownOpen && (
                            <div className="absolute left-0 mt-2 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden" data-testid="all-doctor-mode-specialty-dropdown">
                                {/* Search */}
                                <div className="p-2 border-b border-gray-200">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-testid="all-doctor-mode-specialty-search-icon" />
                                        <input
                                            ref={specialtySearchInputRef}
                                            type="text"
                                            value={specialtySearchQuery}
                                            onChange={handleSpecialtySearchChange}
                                            placeholder={t('patientappointment.search_specialty', 'Search specialty...')}
                                            maxLength={MAX_SPECIALTY_SEARCH_LENGTH}
                                            className={`w-full pl-10 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${specialtySearchError
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-300'
                                                }`}
                                            autoFocus
                                            data-testid="all-doctor-mode-specialty-search-input"
                                        />
                                    </div>
                                    {specialtySearchError && (
                                        <p className="text-red-500 text-xs mt-1" data-testid="all-doctor-mode-specialty-search-error">{specialtySearchError}</p>
                                    )}
                                    {specialtySearchQuery.length > 0 && !specialtySearchError && (
                                        <p className="text-gray-400 text-xs mt-1" data-testid="all-doctor-mode-specialty-search-counter">
                                            {specialtySearchQuery.length}/{MAX_SPECIALTY_SEARCH_LENGTH} {t('patientappointment.characters', 'characters')}
                                        </p>
                                    )}
                                </div>

                                {/* Loading */}
                                {specialtyLoading && (
                                    <div className="p-4 flex justify-center" data-testid="all-doctor-mode-specialty-list-loading">
                                        <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                    </div>
                                )}

                                {/* List */}
                                {!specialtyLoading && (
                                    <ul className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-[#4ABDBD] scrollbar-track-gray-100" data-testid="all-doctor-mode-specialty-list">
                                        {(!specialties || specialties.length === 0) ? (
                                            <li className="px-4 py-3 text-gray-500 text-center" data-testid="all-doctor-mode-no-specialties-text">
                                                {t('patientappointment.no_specialties_found', 'No specialties found')}
                                            </li>
                                        ) : (
                                            specialties.map((specialty, idx) => {
                                                const specialtyId = specialty.specialtyId || specialty.id || specialty._id;
                                                return (
                                                    <li
                                                        key={specialtyId || idx}
                                                        onClick={() => handleSpecialtySelect(specialty)}
                                                        className={`px-4 py-2.5 transition flex justify-between items-center
                                                        ${idx !== specialties.length - 1 ? 'border-b border-gray-100' : ''}
                                                        cursor-pointer hover:bg-[#4ABDBD] hover:text-white`}
                                                        data-testid={`all-doctor-mode-specialty-option-${specialtyId || idx}`}
                                                    >
                                                        <span>
                                                            <MasterLabel
                                                                type="masterSpecialty"
                                                                id={specialtyId}
                                                                field="name"
                                                                fallback={specialty.name || specialty.specialtyName || '-'}
                                                            />
                                                        </span>
                                                    </li>
                                                );
                                            })
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div data-testid="all-doctor-mode-date-field-container">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="all-doctor-mode-date-label">{t('patientappointment.select_date', 'Select Date')} <span className="text-red-500">*</span></label>
                    <CustomDatePicker
                        value={selectedDate}
                        onChange={(e) => {
                            setSelectedDate(e.target.value);
                            setCurrentPage(1);
                            // Reset doctor selections, drawer, and slots when date changes
                            setOpenDoctorDrawer(null);
                            setDoctorSelections({});
                            setSelectedDoctorForSlots(null);
                        }}
                        minDate={format(today, 'yyyy-MM-dd')}
                        placeholder={t('patientappointment.select_a_date', 'Select a date')}
                    />
                </div>
            </div>

            {/* All Doctor Mode - Doctors List */}
            {selectedSpecialtyId && selectedDate && (
                <div data-testid="all-doctor-mode-doctors-section">
                    {isAvailableDoctorsLoading ? (
                        <div className="flex flex-col items-center justify-center py-8 min-h-[200px]" data-testid="all-doctor-mode-loading-doctors">
                            <CommonLoader />
                            <span className="text-xl text-[#4ABDBD] font-semibold mt-4" data-testid="all-doctor-mode-loading-doctors-text">{t('patientappointment.loading_doctors', 'Loading doctors...')}</span>
                        </div>
                    ) : availableDoctors && availableDoctors.length > 0 ? (
                        <>
                            <div className="space-y-3 mb-3" data-testid="all-doctor-mode-doctors-list">
                                {availableDoctors.map((doctor) => {
                                    const doctorId = doctor.doctorId || doctor._id;
                                    const currentSelections = doctorSelections[doctorId] || { appointmentType: '', address: '' };

                                    return (
                                        <div
                                            key={doctorId}
                                            className="w-full bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all duration-300 hover:border-[#4ABDBD]"
                                            data-testid={`all-doctor-mode-doctor-card-${doctorId}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                {/* Doctor Avatar */}
                                                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] flex items-center justify-center text-white font-semibold text-base flex-shrink-0 overflow-hidden relative" data-testid={`all-doctor-mode-doctor-avatar-${doctorId}`}>
                                                    {doctor.image ? (
                                                        <img
                                                            src={getFileURL(doctor.image)}
                                                            alt={doctor.name || `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() || t('patientappointment.doctor_alt', 'Doctor')}
                                                            className="w-full h-full object-cover rounded-full"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const fallback = e.target.parentElement.querySelector('.avatar-fallback');
                                                                if (fallback) fallback.style.display = 'flex';
                                                            }}
                                                            data-testid={`all-doctor-mode-doctor-avatar-image-${doctorId}`}
                                                        />
                                                    ) : null}
                                                    <div className={`w-full h-full flex items-center justify-center avatar-fallback ${doctor.image ? 'hidden' : ''}`} data-testid={`all-doctor-mode-doctor-avatar-fallback-${doctorId}`}>
                                                        {doctor.name ? doctor.name.charAt(0).toUpperCase() : (doctor.firstName ? doctor.firstName.charAt(0).toUpperCase() : 'D')}
                                                    </div>
                                                </div>
                                                {/* Doctor Info */}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-base font-semibold text-gray-800 mb-0.5 truncate" data-testid={`all-doctor-mode-doctor-name-${doctorId}`}>
                                                        {doctor.name || `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() || t('patientappointment.doctor_alt', 'Doctor')}
                                                    </h3>
                                                    <div className="flex flex-wrap gap-3 text-xs text-gray-600" data-testid={`all-doctor-mode-doctor-info-${doctorId}`}>
                                                        {doctor.specialty && (
                                                            <div className="flex items-center gap-1" data-testid={`all-doctor-mode-doctor-specialty-${doctorId}`}>
                                                                <Award size={14} className="text-[#4ABDBD]" data-testid={`all-doctor-mode-doctor-specialty-icon-${doctorId}`} />
                                                                <span className="truncate" data-testid={`all-doctor-mode-doctor-specialty-text-${doctorId}`}>{doctor.specialty}</span>
                                                            </div>
                                                        )}
                                                        {doctor.email && (
                                                            <div className="flex items-center gap-1" data-testid={`all-doctor-mode-doctor-email-${doctorId}`}>
                                                                <Mail size={14} className="text-[#4ABDBD]" data-testid={`all-doctor-mode-doctor-email-icon-${doctorId}`} />
                                                                <span className="truncate max-w-[200px]" data-testid={`all-doctor-mode-doctor-email-text-${doctorId}`}>{doctor.email}</span>
                                                            </div>
                                                        )}
                                                        {doctor.phone && (
                                                            <div className="flex items-center gap-1" data-testid={`all-doctor-mode-doctor-phone-${doctorId}`}>
                                                                <Phone size={14} className="text-[#4ABDBD]" data-testid={`all-doctor-mode-doctor-phone-icon-${doctorId}`} />
                                                                <span data-testid={`all-doctor-mode-doctor-phone-text-${doctorId}`}>{doctor.phone}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* Select Button */}
                                                <button
                                                    onClick={() => {
                                                        if (openDoctorDrawer === doctorId) {
                                                            setOpenDoctorDrawer(null);
                                                            // Clear selections for this doctor when closing
                                                            setDoctorSelections(prev => {
                                                                const updated = { ...prev };
                                                                delete updated[doctorId];
                                                                return updated;
                                                            });
                                                            // Clear selected doctor for slots if it was this doctor
                                                            if (selectedDoctorForSlots && selectedDoctorForSlots.doctorId === doctorId) {
                                                                setSelectedDoctorForSlots(null);
                                                            }
                                                        } else {
                                                            // If another doctor drawer is open, reset its fields
                                                            if (openDoctorDrawer && openDoctorDrawer !== doctorId) {
                                                                setDoctorSelections(prev => {
                                                                    const updated = { ...prev };
                                                                    delete updated[openDoctorDrawer];
                                                                    return updated;
                                                                });
                                                                if (selectedDoctorForSlots && selectedDoctorForSlots.doctorId === openDoctorDrawer) {
                                                                    setSelectedDoctorForSlots(null);
                                                                }
                                                            }
                                                            setOpenDoctorDrawer(doctorId);
                                                            // Initialize selections for this doctor
                                                            setDoctorSelections(prev => ({
                                                                ...prev,
                                                                [doctorId]: { appointmentType: '', address: '' }
                                                            }));
                                                        }
                                                    }}
                                                    className="px-5 py-1.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg font-semibold text-sm hover:shadow-md transition-all duration-300 hover:scale-105 cursor-pointer flex-shrink-0"
                                                    data-testid={`all-doctor-mode-select-doctor-button-${doctorId}`}
                                                >
                                                    {openDoctorDrawer === doctorId ? t('patientappointment.close', 'Close') : t('patientappointment.select', 'Select')}
                                                </button>
                                            </div>

                                            {/* Dropdown Drawer */}
                                            {openDoctorDrawer === doctorId && (
                                                <div className="mt-3 pt-3 border-t border-gray-200" data-testid={`all-doctor-mode-doctor-drawer-${doctorId}`}>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                                        {/* Appointment Type Select */}
                                                        <div data-testid={`all-doctor-mode-appointment-type-field-container-${doctorId}`}>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2" data-testid={`all-doctor-mode-appointment-type-label-${doctorId}`}>
                                                                {t('patientappointment.appointment_type', 'Appointment Type')} <span className="text-red-500">*</span>
                                                            </label>
                                                            <select
                                                                value={currentSelections.appointmentType}
                                                                onChange={(e) => {
                                                                    setDoctorSelections(prev => ({
                                                                        ...prev,
                                                                        [doctorId]: {
                                                                            ...prev[doctorId],
                                                                            appointmentType: e.target.value
                                                                        }
                                                                    }));
                                                                }}
                                                                className="w-full p-3 border rounded-xl bg-white text-gray-700 focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none transition-all duration-300 text-sm border-gray-200"
                                                                data-testid={`all-doctor-mode-appointment-type-select-${doctorId}`}
                                                            >
                                                                <option value="" disabled>{t('patientappointment.select_appointment_type', 'Select Appointment Type')}</option>
                                                                {doctor.appointmentTypes && doctor.appointmentTypes.length > 0 ? (
                                                                    doctor.appointmentTypes.map((apptType) => (
                                                                        <option key={apptType.typeId} value={apptType.typeId}>
                                                                            {apptType.type}
                                                                        </option>
                                                                    ))
                                                                ) : (
                                                                    <option value="" disabled>{t('patientappointment.no_appointment_types', 'No appointment types available')}</option>
                                                                )}
                                                            </select>
                                                        </div>

                                                        {/* Address Select */}
                                                        <div data-testid={`all-doctor-mode-address-field-container-${doctorId}`}>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2" data-testid={`all-doctor-mode-address-label-${doctorId}`}>
                                                                {t('patientappointment.address', 'Address')} <span className="text-red-500">*</span>
                                                            </label>
                                                            <select
                                                                value={currentSelections.address}
                                                                onChange={(e) => {
                                                                    setDoctorSelections(prev => ({
                                                                        ...prev,
                                                                        [doctorId]: {
                                                                            ...prev[doctorId],
                                                                            address: e.target.value
                                                                        }
                                                                    }));
                                                                }}
                                                                className="w-full p-3 border rounded-xl bg-white text-gray-700 focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none transition-all duration-300 text-sm border-gray-200"
                                                                data-testid={`all-doctor-mode-address-select-${doctorId}`}
                                                            >
                                                                <option value="" disabled>{t('patientappointment.select_address', 'Select Address')}</option>
                                                                {doctor.addresses && doctor.addresses.length > 0 ? (
                                                                    doctor.addresses.map((address) => (
                                                                        <option key={address.addressId} value={address.addressId}>
                                                                            {address.addressType}
                                                                        </option>
                                                                    ))
                                                                ) : (
                                                                    <option value="" disabled>{t('patientappointment.no_addresses_available', 'No addresses available')}</option>
                                                                )}
                                                            </select>
                                                        </div>
                                                    </div>

                                                    {/* Display Available Slots */}
                                                    {selectedDoctorForSlots && selectedDoctorForSlots.doctorId === doctorId && singleDaySlots && (
                                                        <div className="mt-4 pt-4 border-t border-gray-200" data-testid={`all-doctor-mode-slots-section-${doctorId}`}>
                                                            <div className="mb-3" data-testid={`all-doctor-mode-slots-header-${doctorId}`}>
                                                                <h4 className="text-base font-semibold text-gray-800 mb-1" data-testid={`all-doctor-mode-slots-title-${doctorId}`}>
                                                                    {t('patientappointment.available_slots_for', 'Available Slots for')} {singleDaySlots.day}, {format(new Date(singleDaySlots.date), 'MMM d, yyyy')}
                                                                </h4>
                                                                <p className="text-sm text-gray-600" data-testid={`all-doctor-mode-slots-subtitle-${doctorId}`}>
                                                                    {t('patientappointment.select_time_slot', 'Select a time slot to proceed with booking')}
                                                                </p>
                                                            </div>

                                                            {isSingleDaySlotsLoading ? (
                                                                <div className="flex justify-center py-4" data-testid={`all-doctor-mode-slots-loading-${doctorId}`}>
                                                                    <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                                                </div>
                                                            ) : singleDaySlots.slots && singleDaySlots.slots.length > 0 ? (
                                                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-60 overflow-y-auto" data-testid={`all-doctor-mode-slots-grid-${doctorId}`}>
                                                                    {singleDaySlots.slots
                                                                        .filter(slot => slot.status === 'available')
                                                                        .map((slot, idx) => {
                                                                            const selectedApptTypeObj = doctor.appointmentTypes?.find(apt => apt.typeId === currentSelections.appointmentType);
                                                                            const appointmentTypeName = selectedApptTypeObj?.type || '';
                                                                            const specialtyName = selectedSpecialty?.name || doctor.specialtyName || '';

                                                                            return (
                                                                                <button
                                                                                    key={idx}
                                                                                    type="button"
                                                                                    onClick={async () => {
                                                                                        const slotStart = new Date(slot.start);
                                                                                        const slotEnd = new Date(slot.end);
                                                                                        setSelectedSlot({
                                                                                            start: slotStart.toISOString(),
                                                                                            end: slotEnd.toISOString()
                                                                                        });

                                                                                        setSelectedAddressId(currentSelections.address);
                                                                                        setSelectedAppointmentType(appointmentTypeName);
                                                                                        setSelectedSpeciality(specialtyName);

                                                                                        isFromAllDoctorMode.current = true;
                                                                                        setSelectedDoctorId(doctorId);

                                                                                        await dispatch(getDoctorById(doctorId));
                                                                                        isFromAllDoctorMode.current = false;

                                                                                        if (selectedPatient?.patientId || Id) {
                                                                                            const patientId = selectedPatient?.patientId || Id;
                                                                                            const specialtyId = selectedSpecialtyId;
                                                                                            const appointmentTypeId = currentSelections.appointmentType;

                                                                                            if (patientId && doctorId && specialtyId && appointmentTypeId) {
                                                                                                await dispatch(getSingleCustomPrice({
                                                                                                    patientId,
                                                                                                    doctorId,
                                                                                                    specialityId: specialtyId,
                                                                                                    appointmentTypeId
                                                                                                }));
                                                                                            }
                                                                                        }

                                                                                        setIsModalOpen(true);

                                                                                        // Don't close drawer here - wait for successful booking
                                                                                    }}
                                                                                    className="p-2 bg-gray-50 border border-gray-200 rounded-lg text-center text-sm hover:bg-[#4ABDBD] hover:text-white hover:border-[#4ABDBD] cursor-pointer transition-all duration-200"
                                                                                    data-testid={`all-doctor-mode-slot-button-${doctorId}-${idx}`}
                                                                                >
                                                                                    {format(new Date(slot.start), 'h:mm a')}
                                                                                </button>
                                                                            );
                                                                        })}
                                                                </div>
                                                            ) : (
                                                                <div className="text-center py-4 text-gray-500 text-sm" data-testid={`all-doctor-mode-no-slots-text-${doctorId}`}>
                                                                    {t('patientappointment.no_slots_for_date', 'No available slots for this date')}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Pagination */}
                            {availableDoctorsMetadata.total > 0 && (
                                <Pagination
                                    totalItems={availableDoctorsMetadata.total}
                                    currentPage={currentPage}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={(page, limit) => {
                                        setCurrentPage(page);
                                        setItemsPerPage(limit);
                                    }}
                                />
                            )}
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-8 min-h-[200px]" data-testid="all-doctor-mode-no-doctors-state">
                            <img
                                src={noslotts}
                                alt={t('patientappointment.no_doctors_available', 'No doctors available')}
                                className="w-36 h-36 sm:w-44 sm:h-44 md:w-60 md:h-60 object-contain"
                                data-testid="all-doctor-mode-no-doctors-image"
                            />
                            <p className="text-gray-500 text-center text-sm sm:text-base md:text-lg mt-4" data-testid="all-doctor-mode-no-doctors-text">
                                {t('patientappointment.no_doctors_for_selection', 'No doctors available for the selected specialty and date')}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* All Doctor Mode - Empty State */}
            {!selectedSpecialtyId || !selectedDate ? (
                <div className="flex flex-col items-center justify-center py-8 min-h-[200px]" data-testid="all-doctor-mode-select-state">
                    <img
                        src={calender_empty}
                        alt={t('patientappointment.select_specialty_date', 'Select specialty and date')}
                        className="w-36 h-36 sm:w-44 sm:h-44 md:w-60 md:h-60 object-contain"
                        data-testid="all-doctor-mode-select-state-image"
                    />
                    <p className="text-gray-500 text-center text-sm sm:text-base md:text-lg mt-4" data-testid="all-doctor-mode-select-state-text">
                        {t('patientappointment.please_select_specialty_date', 'Please select a specialty and date to view available doctors')}
                    </p>
                </div>
            ) : null}
        </>
    );
};

export default AllDoctorMode;

